import type { RouteRecordRaw } from "vue-router";

/**
 * 递归扁平化路由树，将所有叶子页面提取为 AdminLayout 的直接子路由
 * 从根本上解决 Vue Router 多级嵌套路由导致 keep-alive 缓存失效与白屏的问题
 */
export function flattenAdminRoutes(routes: RouteRecordRaw[], basePath = ""): RouteRecordRaw[] {
    const flatList: RouteRecordRaw[] = [];

    routes.forEach((route) => {
        const fullPath = route.path.startsWith("/")
            ? route.path
            : basePath
                ? `${basePath.endsWith("/") ? basePath.slice(0, -1) : basePath}/${route.path}`
                : route.path;

        if (route.children && route.children.length > 0) {
            // 若包含重定向配置，也注册该路径以支持父路径正确跳转
            if (route.redirect) {
                flatList.push({
                    path: fullPath,
                    redirect: route.redirect
                } as RouteRecordRaw);
            }
            // 递归提取子路由
            const subRoutes = flattenAdminRoutes(route.children, fullPath);
            flatList.push(...subRoutes);
        } else if (route.component) {
            // 叶子节点直接作为扁平路由注册给 Vue Router
            flatList.push({
                ...route,
                path: fullPath
            });
        }
    });

    return flatList;
}

/** 生产环境隐藏订单管理和库位管理。storage 在商品管理的子路由里，需要递归过滤 */
const PROD_HIDDEN_MENU_PATHS = ["retail-order", "storage"];

function hideProdMenus(route: RouteRecordRaw): RouteRecordRaw {
    if (!route.children?.length) {
        return route;
    }
    const children = route.children
        .filter((child) => import.meta.env.VITE_ENV !== "prod" || !PROD_HIDDEN_MENU_PATHS.includes(child.path))
        .map((child) => hideProdMenus(child));
    return {
        ...route,
        children
    };
}

/**
 * 后台层级菜单树（用于侧边栏 Sidebar 渲染）
 * 即时零售后台 v2.0 菜单结构
 */
export const adminMenuTree: RouteRecordRaw[] = [
    {
        path: "/pcadmin",
        name: "AdminLayout",
        component: () => import("@/views/admin/layout/index.vue"),
        redirect: "/pcadmin/dashboard",
        meta: {
            title: "即时零售",
            icon: "Platform",
            isLogin: true,
            isHeader: true
        },
        children: [
            // ========== 首页（不在侧边栏显示，但可通过 TagsView 访问）==========
            {
                path: "dashboard",
                name: "AdminDashboard",
                component: () => import("@/views/admin/dashboard/index.vue"),
                meta: {
                    title: "首页",
                    icon: "House",
                    affix: true,
                    keepAlive: true,
                    hidden: true,
                    isLogin: true,
                    isHeader: true
                }
            },

            // ========== 1. 商品管理 ==========
            {
                path: "product",
                name: "AdminProduct",
                redirect: "/pcadmin/product/list",
                meta: {
                    title: "商品管理",
                    icon: "Goods",
                    alwaysShow: true,
                    isLogin: true,
                    isHeader: true,
                    authKey: [4]
                },
                children: [
                    {
                        path: "list",
                        name: "AdminProductList",
                        component: () => import("@/views/admin/instant-retail/product/list.vue"),
                        meta: {
                            title: "商品列表",
                            icon: "Document",
                            keepAlive: true,
                            isLogin: true,
                            isHeader: true
                        }
                    },
                    {
                        path: "storage",
                        name: "AdminProductStorage",
                        component: () => import("@/views/admin/instant-retail/product/storage.vue"),
                        meta: {
                            title: "库位管理",
                            icon: "TakeawayBox",
                            keepAlive: true,
                            isLogin: true,
                            isHeader: true
                        }
                    },
                    {
                        path: "create-edit",
                        name: "AdminProductCreateEdit",
                        component: () => import("@/views/admin/instant-retail/product/create-edit.vue"),
                        meta: {
                            title: "创建/编辑商品",
                            hidden: true,
                            activeMenu: "/pcadmin/product/list",
                            keepAlive: true,
                            isLogin: true,
                            isHeader: true
                        }
                    }
                ]
            },

            // ========== 2. 订单管理（即时零售） ==========
            {
                path: "retail-order",
                name: "AdminRetailOrderGroup",
                redirect: "/pcadmin/retail-order/index",
                meta: {
                    title: "订单管理",
                    icon: "Tickets",
                    alwaysShow: false,
                    isLogin: true,
                    isHeader: true,
                    authKey: [7]
                },
                children: [
                    {
                        path: "index",
                        name: "AdminRetailOrder",
                        component: () => import("@/views/admin/instant-retail/retail-order/index.vue"),
                        meta: {
                            title: "订单管理",
                            icon: "Tickets",
                            keepAlive: true,
                            isLogin: true,
                            isHeader: true
                        }
                    }
                ]
            },

            // ========== 3. 建议补货 ==========
            {
                path: "restock",
                name: "AdminRestockGroup",
                redirect: "/pcadmin/restock/index",
                meta: {
                    title: "建议补货",
                    icon: "Document",
                    alwaysShow: false,
                    isLogin: true,
                    isHeader: true,
                    authKey: [9]
                },
                children: [
                    {
                        path: "index",
                        name: "AdminRestock",
                        component: () => import("@/views/admin/instant-retail/restock/index.vue"),
                        meta: {
                            title: "建议补货",
                            icon: "Document",
                            keepAlive: true,
                            isLogin: true,
                            isHeader: true
                        }
                    }
                ]
            },

            // ========== 4. 自营商城管理（迁入现有所有业务模块） ==========
            {
                path: "mall",
                name: "AdminMall",
                redirect: "/pcadmin/mall/goods/syncGoods",
                meta: {
                    title: "自营商城管理",
                    icon: "ShoppingBag",
                    alwaysShow: true,
                    isLogin: true,
                    isHeader: true,
                    authKey: "XCX"
                },
                children: [
                    // --- 商品管理（原 goods） ---
                    {
                        path: "goods",
                        name: "AdminMallGoods",
                        redirect: "/pcadmin/mall/goods/syncGoods",
                        meta: {
                            title: "商品管理",
                            icon: "Grid",
                            alwaysShow: true,
                            isLogin: true,
                            isHeader: true
                        },
                        children: [
                            {
                                path: "syncGoods",
                                name: "AdminSyncGoods",
                                component: () => import("@/views/admin/mini-program/goods/syncGoods.vue"),
                                meta: {
                                    title: "同步商品",
                                    icon: "Document",
                                    keepAlive: true,
                                    isLogin: true,
                                    isHeader: true
                                }
                            }
                        ]
                    },
                    // --- 营销管理（原 marketing） ---
                    {
                        path: "marketing",
                        name: "AdminMallMarketing",
                        redirect: "/pcadmin/mall/marketing/coupon/list",
                        meta: {
                            title: "营销管理",
                            icon: "Histogram",
                            alwaysShow: true,
                            isLogin: true,
                            isHeader: true
                        },
                        children: [
                            {
                                path: "coupon/list",
                                name: "admin-coupon-list",
                                component: () => import("@/views/admin/mini-program/marketing/coupon/list.vue"),
                                meta: {
                                    title: "优惠券管理",
                                    icon: "Discount",
                                    keepAlive: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "welfare",
                                name: "admin-welfare-settings",
                                component: () => import("@/views/admin/mini-program/marketing/welfare/index.vue"),
                                meta: {
                                    title: "福利设置",
                                    icon: "Gift",
                                    keepAlive: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "welfare/group",
                                name: "admin-welfare-group",
                                component: () => import("@/views/admin/mini-program/marketing/welfare/group.vue"),
                                meta: {
                                    title: "入群福利",
                                    icon: "ChatDotRound",
                                    hidden: true,
                                    activeMenu: "/pcadmin/mall/marketing/welfare",
                                    keepAlive: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "welfare/invite",
                                name: "admin-welfare-invite",
                                component: () => import("@/views/admin/mini-program/marketing/welfare/invite.vue"),
                                meta: {
                                    title: "邀请好友福利",
                                    icon: "Share",
                                    hidden: true,
                                    activeMenu: "/pcadmin/mall/marketing/welfare",
                                    keepAlive: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "welfare/registration",
                                name: "admin-welfare-registration",
                                component: () => import("@/views/admin/mini-program/marketing/welfare/registration.vue"),
                                meta: {
                                    title: "注册会员权益",
                                    icon: "User",
                                    hidden: true,
                                    activeMenu: "/pcadmin/mall/marketing/welfare",
                                    keepAlive: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "coupon/create",
                                name: "admin-coupon-create",
                                component: () => import("@/views/admin/mini-program/marketing/coupon/create.vue"),
                                meta: {
                                    title: "新建优惠券",
                                    icon: "Plus",
                                    hidden: true,
                                    activeMenu: "/pcadmin/mall/marketing/coupon/list",
                                    keepAlive: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "coupon/edit/:couponId",
                                name: "admin-coupon-edit",
                                component: () => import("@/views/admin/mini-program/marketing/coupon/edit.vue"),
                                props: true,
                                meta: {
                                    title: "编辑优惠券",
                                    icon: "Edit",
                                    hidden: true,
                                    activeMenu: "/pcadmin/mall/marketing/coupon/list",
                                    keepAlive: true,
                                    cacheByFullPath: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            }
                        ]
                    },
                    // --- 订单管理（原 order） ---
                    {
                        path: "order",
                        name: "AdminMallOrder",
                        redirect: "/pcadmin/mall/order/list",
                        meta: {
                            title: "订单管理",
                            icon: "List",
                            alwaysShow: true,
                            isLogin: true,
                            isHeader: true
                        },
                        children: [
                            {
                                path: "list",
                                name: "admin-order-list",
                                component: () => import("@/views/admin/mini-program/order/list.vue"),
                                meta: {
                                    title: "订单列表",
                                    icon: "Document",
                                    keepAlive: true,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "detail",
                                name: "admin-order-detail",
                                component: () => import("@/views/admin/mini-program/order/detail.vue"),
                                meta: {
                                    title: "订单详情",
                                    icon: "Document",
                                    hidden: true,
                                    activeMenu: "/pcadmin/mall/order/list",
                                    keepAlive: true,
                                    cacheByFullPath: true,
                                    isLogin: true,
                                    isHeader: true
                                }
                            }
                        ]
                    },
                    // --- 会员管理（原 member） ---
                    {
                        path: "member",
                        name: "AdminMallMember",
                        redirect: "/pcadmin/mall/member/list",
                        meta: {
                            title: "会员管理",
                            icon: "User",
                            alwaysShow: true,
                            isLogin: true,
                            isHeader: true
                        },
                        children: [
                            {
                                path: "list",
                                name: "admin-member-list",
                                component: () => import("@/views/admin/mini-program/member/list.vue"),
                                meta: {
                                    title: "会员列表",
                                    icon: "Document",
                                    keepAlive: true,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "detail",
                                name: "admin-member-detail",
                                component: () => import("@/views/admin/mini-program/member/detail.vue"),
                                meta: {
                                    title: "会员详情",
                                    icon: "Document",
                                    hidden: true,
                                    activeMenu: "/pcadmin/mall/member/list",
                                    keepAlive: true,
                                    cacheByFullPath: true,
                                    isLogin: true,
                                    isHeader: true
                                }
                            }
                        ]
                    },
                    // --- 数据报表（原 report） ---
                    {
                        path: "report",
                        name: "AdminMallReport",
                        redirect: "/pcadmin/mall/report/syncGoodsTask",
                        meta: {
                            title: "数据报表",
                            icon: "DataAnalysis",
                            alwaysShow: true,
                            isLogin: true,
                            isHeader: true
                        },
                        children: [
                            {
                                path: "syncGoodsTask",
                                name: "admin-sync-goods-task",
                                component: () => import("@/views/admin/mini-program/report/syncGoodsTask/index.vue"),
                                meta: {
                                    title: "同步商品任务",
                                    icon: "Document",
                                    keepAlive: true,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "activity",
                                name: "admin-activity-report",
                                component: () => import("@/views/admin/mini-program/report/activity/index.vue"),
                                meta: {
                                    title: "活动查看",
                                    icon: "Tickets",
                                    keepAlive: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            }
                        ]
                    },
                    // --- 系统管理（原 system） ---
                    {
                        path: "system",
                        name: "AdminMallSystem",
                        redirect: "/pcadmin/mall/system/merchant-setting",
                        meta: {
                            title: "系统管理",
                            icon: "Setting",
                            alwaysShow: true,
                            isLogin: true,
                            isHeader: true
                        },
                        children: [
                            {
                                path: "merchant-setting",
                                name: "admin-system-merchant-setting",
                                component: () => import("@/views/admin/mini-program/system/merchant-setting.vue"),
                                meta: {
                                    title: "商家设置",
                                    icon: "OfficeBuilding",
                                    keepAlive: true,
                                    isLogin: true,
                                    isHeader: true
                                }
                            },
                            {
                                path: "delivery-setting",
                                name: "admin-system-delivery-setting",
                                component: () => import("@/views/admin/mini-program/system/delivery-setting/index.vue"),
                                meta: {
                                    title: "配送服务设置",
                                    icon: "Van",
                                    keepAlive: true,
                                    affix: false,
                                    isLogin: true,
                                    isHeader: true
                                }
                            }
                        ]
                    }
                ]
            },

            // ========== 5. 门店设置 ==========
            {
                path: "store-settings",
                name: "AdminStoreSettingsGroup",
                redirect: "/pcadmin/store-settings/index",
                meta: {
                    title: "门店设置",
                    icon: "Setting",
                    alwaysShow: false,
                    isLogin: true,
                    isHeader: true
                },
                children: [
                    {
                        path: "index",
                        name: "AdminStoreSettings",
                        component: () => import("@/views/admin/instant-retail/store-settings/index.vue"),
                        meta: {
                            title: "门店设置",
                            icon: "Setting",
                            keepAlive: true,
                            isLogin: true,
                            isHeader: true
                        }
                    }
                ]
            }
        ]
    }
].map((route) => hideProdMenus(route));

/**
 * 静态注册进 Vue Router 的后台路由定义
 * 仅包含 AdminLayout 壳 + Dashboard 首页（不受权限控制）
 * 其余业务路由由 permission store 的 generateRoutes 动态注册
 */
export const adminRoutes: RouteRecordRaw[] = [
    {
        path: "/pcadmin",
        name: "AdminLayout",
        component: () => import("@/views/admin/layout/index.vue"),
        redirect: "/pcadmin/dashboard",
        meta: {
            title: "即时零售",
            icon: "Platform",
            isLogin: true,
            isHeader: true
        },
        children: [
            {
                path: "dashboard",
                name: "AdminDashboard",
                component: () => import("@/views/admin/dashboard/index.vue"),
                meta: {
                    title: "首页",
                    icon: "House",
                    affix: true,
                    keepAlive: true,
                    hidden: true,
                    isLogin: true,
                    isHeader: true
                }
            }
        ]
    }
];

export default adminRoutes;
